import axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import OrderNavigationStrip from './OrderNavigationStrip';
import { FaClock, FaEye, FaCheckCircle, FaTimesCircle, FaArrowLeft, FaSearch, FaSync, FaUndo } from 'react-icons/fa';
import AdminPageLoader from './AdminPageLoader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function PendingOrdersPage() {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingStatus, setUpdatingStatus] = useState(null);
    const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'oldest'
    const [refreshCounts, setRefreshCounts] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [socketVersion, setSocketVersion] = useState(0);

    const handleApiError = (error) => {
        console.error('API Error details:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const fetchPendingOrders = async (showLoader = true) => {
        if (showLoader) setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${API_BASE_URL}/pending-orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(response.data);
            
            // apply search and sort
            let result = [...response.data];
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                result = result.filter(o => 
                    (o.orderNumber && o.orderNumber.toLowerCase().includes(searchLower)) ||
                    (o.address?.name && o.address.name.toLowerCase().includes(searchLower)) ||
                    (o.mobile && o.mobile.toString().includes(searchLower))
                );
            }
            result.sort((a, b) => {
                const dateA = new Date(a.createdAt);
                const dateB = new Date(b.createdAt);
                return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
            });
            setFilteredOrders(result);
        } catch (error) {
            handleApiError(error);
        } finally {
            if (showLoader) setLoading(false);
        }
    };

    const handleSearchChange = (val) => {
        setSearchTerm(val);
        const searchLower = val.toLowerCase();
        let result = [...orders];
        if (val) {
            result = result.filter(o => 
                (o.orderNumber && o.orderNumber.toLowerCase().includes(searchLower)) ||
                (o.address?.name && o.address.name.toLowerCase().includes(searchLower)) ||
                (o.mobile && o.mobile.toString().includes(searchLower))
            );
        }
        result.sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });
        setFilteredOrders(result);
    };

    const handleSortChange = (newSortOrder) => {
        setSortOrder(newSortOrder);
        let result = [...filteredOrders];
        result.sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return newSortOrder === 'newest' ? dateB - dateA : dateA - dateB;
        });
        setFilteredOrders(result);
    };

    const handleReset = () => {
        setSearchTerm('');
        setSortOrder('newest');
        const sorted = [...orders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setFilteredOrders(sorted);
    };

    const handleBackButtonClick = () => {
        setSortOrder('newest');
        setSearchTerm('');
        const sorted = [...orders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setFilteredOrders(sorted);
    };

    const updateOrderStatus = async (orderId, newStatus) => {
        setUpdatingStatus(orderId);
        try {
            const token = localStorage.getItem('authToken');
            await axios.put(`${API_BASE_URL}/update-order-status/${orderId}`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Refresh the orders list
            await fetchPendingOrders();
            setRefreshCounts(prev => !prev);
            alert(`Order status updated to ${newStatus}`);
        } catch (error) {
            handleApiError(error);
        } finally {
            setUpdatingStatus(null);
        }
    };

    useEffect(() => {
        fetchPendingOrders(true);
        
        const socket = io(API_BASE_URL);
        
        socket.on('new-order', (order) => {
            console.log('Socket new-order received in PendingOrdersPage:', order);
            setSocketVersion(prev => prev + 1);
        });

        return () => {
            socket.disconnect();
        };
    }, []);

    useEffect(() => {
        if (socketVersion > 0) {
            fetchPendingOrders(false);
        }
    }, [socketVersion]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">

                {/* Page Header */}
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">PENDING ORDERS</h4>
                                <p className="text-muted p-0 m-0">
                                    Manage orders that are waiting for confirmation
                                </p>
                            </div>
                            <Link 
                                to="/admin/orders" 
                                className="btn theme-btn-blue-flat btn-sm rounded-0"
                                style={{
                                    padding: '6px 16px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                                title="Back to Orders"
                            >
                                <FaArrowLeft size={11} />
                                Back to Orders
                            </Link>
                        </div>
                    </div>
                </div>

                <OrderNavigationStrip activeStatus="pending" refreshTrigger={refreshCounts} />

                {loading ? (
                    <AdminPageLoader message="Loading pending orders..." />
                ) : (
                    <>
                        {/* Control Panel (Search, Sort & Actions Row) - Edge to Edge */}
                        <div className="control-panel-bar">
                            <div className="control-panel-search">
                                <span 
                                    className="position-absolute top-50 translate-middle-y start-0 ps-3 text-muted"
                                    style={{ pointerEvents: 'none' }}
                                >
                                    <FaSearch style={{ fontSize: '14px' }} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control form-control-sm ps-5"
                                    placeholder="Search Order No / Name / Mobile..."
                                    style={{
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        height: '38px',
                                        fontSize: '14px',
                                        background: '#ffffff',
                                        width: '100%'
                                    }}
                                    value={searchTerm}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                />
                            </div>
                            
                            <div className="control-panel-sort">
                                <select
                                    className="form-select form-select-sm"
                                    style={{
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        height: '38px',
                                        fontSize: '14px',
                                        color: '#4b5563',
                                        fontWeight: 500,
                                        background: '#ffffff',
                                        width: '100%'
                                    }}
                                    value={sortOrder}
                                    onChange={(e) => handleSortChange(e.target.value)}
                                >
                                    <option value="newest">Newest First</option>
                                    <option value="oldest">Oldest First</option>
                                </select>
                            </div>

                            <div className="control-panel-reset">
                                <button
                                    className="btn-reset-square"
                                    title="Reset Filters"
                                    onClick={handleReset}
                                >
                                    <FaUndo style={{ fontSize: '14px' }} />
                                </button>
                            </div>
                        </div>

                        {/* Orders Table - Edge to Edge */}
                        <div className="row orders-table-row">
                            <div className="col-12">
                                {filteredOrders.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-hover orders-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '15%', minWidth: '100px' }}>Order Info</th>
                                                    <th style={{ width: '35%', minWidth: '220px' }}>Customer</th>
                                                    <th style={{ width: '20%', minWidth: '140px' }}>Billing</th>
                                                    <th style={{ width: '15%', minWidth: '130px' }}>Order Date</th>
                                                    <th style={{ width: '15%', minWidth: '100px' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredOrders.map((item, index) => {
                                                    let orderType = 'COD';
                                                    if (item.paymentMethod === 'paynow' && item.paymentStatus === 'paid') orderType = 'PAID';
                                                    else if (item.paymentMethod === 'paynow' && item.paymentStatus !== 'paid') orderType = 'PAYNOW';
                                                    return (
                                                        <tr key={item._id}>
                                                            <td>
                                                                <div className="d-flex flex-column align-items-start">
                                                                    <span style={{ fontWeight: 600, color: '#1a237e', fontSize: '14px' }}>
                                                                        {item.orderNumber || `best-${item._id?.slice(-5)}`}
                                                                    </span>
                                                                    <span className="text-muted" style={{ fontSize: '11px', marginTop: '2px' }}>
                                                                        Sr. #{index + 1}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div>
                                                                    <span style={{ fontWeight: 600, color: '#111827', fontSize: '14px' }}>{item.address?.name || 'N/A'}</span>
                                                                    {item.mobile && (
                                                                        <div className="text-muted" style={{ fontSize: '12px', marginTop: '1px' }}>
                                                                            {item.mobile}
                                                                        </div>
                                                                    )}
                                                                    <small className="text-muted d-block mt-1" style={{ fontSize: '12px', lineHeight: '1.4' }}>
                                                                        {item.address ? `${item.address.house}, ${item.address.area}` : 'Address not available'}
                                                                    </small>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div>
                                                                    <span style={{ fontWeight: 600, color: '#111827', fontSize: '15px' }}>
                                                                        {formatCurrency(item.totalAmount || 0)}
                                                                    </span>
                                                                    <div className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: '12px' }}>
                                                                        <div className="item-count-hover-container">
                                                                            <span 
                                                                                className="text-primary fw-medium" 
                                                                                style={{ 
                                                                                    cursor: 'pointer', 
                                                                                    textDecoration: 'underline', 
                                                                                    textDecorationStyle: 'dotted'
                                                                                }}
                                                                            >
                                                                                {item.itemsCount ?? item.items?.length ?? 0} items
                                                                            </span>
                                                                            {item.items && item.items.length > 0 && (
                                                                                <div 
                                                                                    className="item-count-hover-popup"
                                                                                    style={{
                                                                                        position: 'absolute',
                                                                                        top: '50%',
                                                                                        left: '100%',
                                                                                        transform: 'translateY(-50%)',
                                                                                        marginLeft: '8px',
                                                                                        backgroundColor: '#ffffff',
                                                                                        border: '1px solid #cbd5e1',
                                                                                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                                                                        padding: '10px 14px',
                                                                                        zIndex: 100,
                                                                                        width: 'max-content',
                                                                                        minWidth: '180px',
                                                                                        maxWidth: '280px',
                                                                                        pointerEvents: 'none',
                                                                                        textAlign: 'left',
                                                                                        display: 'none'
                                                                                    }}
                                                                                >
                                                                                    <div style={{ fontWeight: '700', fontSize: '11px', color: '#1a237e', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px', marginBottom: '6px', letterSpacing: '0.5px' }}>
                                                                                        ITEMS LIST
                                                                                    </div>
                                                                                    <ul style={{ listStyleType: 'none', padding: 0, margin: 0, fontSize: '11px', color: '#334155', lineHeight: '1.5' }}>
                                                                                        {item.items.map((prod, pIdx) => (
                                                                                            <li key={pIdx} style={{ padding: '2px 0', borderBottom: pIdx < item.items.length - 1 ? '1px dashed #f1f5f9' : 'none' }}>
                                                                                                • {prod.name} <strong style={{ color: '#0f172a' }}>x{prod.quantity}</strong>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <span className="text-muted">•</span>
                                                                        <span style={{ 
                                                                            color: orderType === 'PAID' ? '#16a34a' : '#d97706', 
                                                                            fontWeight: 600 
                                                                        }}>
                                                                            {orderType}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="text-muted" style={{ fontSize: '13px' }}>
                                                                    {formatDate(item.createdAt)}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <Link
                                                                        to={`/admin/orders-details/${item._id}`}
                                                                        className="btn-action-square btn-action-view"
                                                                        title="View Details"
                                                                    >
                                                                        <FaEye style={{ fontSize: '1rem' }} />
                                                                    </Link>
                                                                    <button
                                                                        className="btn-action-square btn-action-accept"
                                                                        onClick={() => updateOrderStatus(item._id, 'accepted')}
                                                                        disabled={updatingStatus === item._id}
                                                                        title="Accept Order"
                                                                    >
                                                                        {updatingStatus === item._id ? (
                                                                            <span className="spinner-border spinner-border-sm" />
                                                                        ) : (
                                                                            <FaCheckCircle style={{ fontSize: '1rem' }} />
                                                                        )}
                                                                    </button>
                                                                    <button
                                                                        className="btn-action-square btn-action-cancel"
                                                                        onClick={() => updateOrderStatus(item._id, 'cancelled')}
                                                                        disabled={updatingStatus === item._id}
                                                                        title="Cancel Order"
                                                                    >
                                                                        {updatingStatus === item._id ? (
                                                                            <span className="spinner-border spinner-border-sm" />
                                                                        ) : (
                                                                            <FaTimesCircle style={{ fontSize: '1rem' }} />
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-5 bg-white border-bottom" style={{ borderTop: '1px solid #d1d5db' }}>
                                        <FaClock className="text-muted fs-1 mb-3" />
                                        <h5 className="text-muted">No Pending Orders</h5>
                                        <p className="text-muted">All orders have been processed or there are no new orders.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Fragment>
    );
}

export default PendingOrdersPage;
